import React from "react";
import ReactSelect, {
  type MenuPosition,
  type MultiValue,
  type SingleValue,
  type StylesConfig,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";

type OptionType = {
  label: string;
  value: string;
  disabled?: boolean;
  isDisabled?: boolean;
};

type Props = {
  label: string;
  name: string;
  required?: boolean;
  options: string[] | OptionType[];
  disabled?: boolean;
  defaultvalue?: string | string[];
  placeholder?: string;
  multiple?: boolean;
  isCustomSelect?: boolean;
  customLine?: string;
};

const RhfSelect = ({
  label,
  name,
  required,
  options,
  disabled,
  multiple = false,
  isCustomSelect = false,
  placeholder,
  defaultvalue,
  customLine,
  ...props
}: Props) => {
  console.log("NORMA:L RERENDERS");
  const { control } = useFormContext();
  const watchedValue = useWatch({ control, name });

  const normalizedOptions = React.useMemo<OptionType[]>(() => {
    if (!Array.isArray(options) || options.length === 0) {
      return [];
    }
    if (typeof options[0] === "string") {
      return (options as string[]).map((opt) => ({ label: opt, value: opt }));
    }
    return (options as OptionType[]).map((opt) => ({
      ...opt,
      isDisabled: opt.disabled === true,
    }));
  }, [options]);

  const [customOptions, setCustomOptions] = React.useState<OptionType[]>(normalizedOptions);

  React.useEffect(() => {
    setCustomOptions((prev) => {
      if (!isCustomSelect) {
        return normalizedOptions;
      }
      const optionMap = new Map<string, OptionType>();
      normalizedOptions.forEach((opt) => optionMap.set(opt.value, opt));
      prev.forEach((opt) => {
        if (!optionMap.has(opt.value)) {
          optionMap.set(opt.value, opt);
        }
      });
      const newOptions = Array.from(optionMap.values());
      if (JSON.stringify(prev) === JSON.stringify(newOptions)) {
        return prev;
      }
      return newOptions;
    });
  }, [normalizedOptions, isCustomSelect]);

  React.useEffect(() => {
    if (!isCustomSelect) return;

    const valuesToKeep = Array.isArray(watchedValue)
      ? watchedValue.filter((v): v is string => typeof v === "string" && v.trim() !== "")
      : typeof watchedValue === "string" && watchedValue.trim() !== ""
        ? [watchedValue]
        : [];

    if (valuesToKeep.length === 0) return;

    setCustomOptions((prev) => {
      const optionMap = new Map(prev.map((opt) => [opt.value, opt]));
      let hasUpdates = false;
      valuesToKeep.forEach((value) => {
        if (!optionMap.has(value)) {
          optionMap.set(value, { label: value, value });
          hasUpdates = true;
        }
      });
      return hasUpdates ? Array.from(optionMap.values()) : prev;
    });
  }, [watchedValue, isCustomSelect]);

  const resolvedPlaceholder =
    typeof placeholder === "string" && placeholder.length > 0
      ? placeholder
      : label
      ? `Select ${label.toLowerCase()} here`
      : "Select an option";

  const resolvedDefaultValue = defaultvalue ?? (multiple ? ([] as string[]) : "");

  const selectStyles = React.useMemo<StylesConfig<OptionType, boolean>>(
    () => ({
      container: (base) => ({
        ...base,
        width: "100%",
      }),
      control: (base, state) => ({
        ...base,
        minHeight: 42,
        borderColor: state.isFocused
          ? "hsl(var(--ring))"
          : base.borderColor ?? "hsl(var(--border))",
        boxShadow: state.isFocused ? "0 0 0 1px hsl(var(--ring))" : "none",
        "&:hover": {
          borderColor: "hsl(var(--ring))",
        },
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        backgroundColor: "transparent",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 99999,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 99999,
      }),
      option: (base, state) => ({
        ...base,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: state.isDisabled ? 0.5 : 1,
      }),
    }),
    [disabled],
  );

  const availableOptions = isCustomSelect ? customOptions : normalizedOptions;

  const menuPortalTarget = typeof document !== "undefined" ? document.body : undefined;
  const menuPosition: MenuPosition = "fixed";

  const stylesSmall = React.useMemo(
    () => ({
      menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
      option: (base: any, state: any) => ({
        ...base,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: state.isDisabled ? 0.5 : 1,
      }),
    }),
    [],
  );

  return (
    <FormField
      control={control}
      name={name}
      defaultValue={resolvedDefaultValue}
      render={({ field }) => {
        // Recompute options based on current field value to handle dynamic disabled states
        const currentOptions = React.useMemo(() => {
          return availableOptions.map(opt => {
            // If the option already has isDisabled set, keep it
            // This allows for dynamic disabled logic based on current selections
            return opt;
          });
        }, [availableOptions]);

        const selectedValueOptions = React.useMemo(() => {
          if (!multiple) {
            if (typeof field.value !== "string" || !field.value) return null;
            const found = availableOptions.find((opt) => opt.value === field.value);
            // Keep showing existing/saved value even if not in the base options list
            return found ?? { label: field.value, value: field.value };
          } else {
            const values = Array.isArray(field.value) ? field.value : [];
            return values.map((value) => {
              const found = currentOptions.find((opt) => opt.value === value);
              return found ?? { label: value, value };
            });
          }
        }, [field.value, availableOptions, currentOptions, multiple]);

        const commonSelectProps = React.useMemo(
          () => ({
            classNamePrefix: "rhf-select",
            inputId: name,
            inputRef: field.ref,
            isDisabled: disabled,
            menuShouldScrollIntoView: false,
            // menuPortalTarget,
            // captureMenuScroll : true,
            menuPosition: "absolute" as const,
            menuPlacement: "auto" as const,
            menuShouldBlockScroll: false,
            closeMenuOnScroll: false,
            placeholder: resolvedPlaceholder,
            styles: stylesSmall,
            onBlur: field.onBlur,
            name: field.name,
            maxMenuHeight: 160,
          }),
          [
            name,
            field.ref,
            disabled,
            menuPortalTarget,
            menuPosition,
            resolvedPlaceholder,
            stylesSmall,
            field.onBlur,
            field.name,
          ],
        );

        const handleSingleChange = React.useCallback(
          (option: SingleValue<OptionType>) => {
            field.onChange(option?.value ?? "");
          },
          [field.onChange],
        );

        const handleMultiChange = React.useCallback(
          (option: MultiValue<OptionType>) => {
            field.onChange(option.map((opt) => opt.value));
          },
          [field.onChange],
        );

        const handleCreateOption = React.useCallback(
          (inputValue: string) => {
            const trimmed = inputValue.trim();
            if (!trimmed) return;
            const newOption = { label: trimmed, value: trimmed };
            setCustomOptions((prev) =>
              prev.some((opt) => opt.value === trimmed)
                ? prev
                : [...prev, newOption],
            );
            if (multiple) {
              const currentValues = Array.isArray(field.value) ? field.value : [];
              if (!currentValues.includes(trimmed)) {
                field.onChange([...currentValues, trimmed]);
              }
              return;
            }
            // Single select: select the created value (keep prior options in the list)
            field.onChange(trimmed);
          },
          [field, multiple],
        );

        return (
          <FormItem className="w-full">
            {label && (
              <FormLabel>
                {label}
                {required ? "*" : ""}
              </FormLabel>
            )}
            <FormControl className="w-full">
              {(() => {
                // SINGLE SELECT - optional creatable
                if (!multiple) {
                  if (isCustomSelect) {
                    return (
                      <CreatableSelect<OptionType, false>
                        {...commonSelectProps}
                        closeMenuOnSelect
                        options={availableOptions}
                        value={selectedValueOptions}
                        onChange={handleSingleChange}
                        isClearable={!required}
                        formatCreateLabel={(inputValue) =>
                          `${customLine ?? "Add"} ${inputValue}`
                        }
                        onCreateOption={handleCreateOption}
                      />
                    );
                  }
                  return (
                    <ReactSelect<OptionType, false>
                      {...commonSelectProps}
                      closeMenuOnSelect
                      options={availableOptions}
                      value={selectedValueOptions}
                      onChange={handleSingleChange}
                      isClearable={!required}
                    />
                  );
                }
                // MULTI SELECT - optional creatable
                const multiProps = {
                  ...commonSelectProps,
                  isMulti: true as const,
                  closeMenuOnSelect: false,
                  options: availableOptions,
                  value: selectedValueOptions,
                  onChange: handleMultiChange,
                };
                if (isCustomSelect) {
                  return (
                    <CreatableSelect<OptionType, true>
                      {...multiProps}
                      formatCreateLabel={(inputValue) =>
                        `${customLine ?? "Add"} ${inputValue}`
                      }
                      onCreateOption={handleCreateOption}
                    />
                  );
                }
                return <ReactSelect<OptionType, true> {...multiProps} />;
              })()}
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default RhfSelect;